<script setup>
/**
 * One control for one of a case type's field definitions.
 *
 * The seven field types collapse into three controls — a checkbox, a native
 * picker, a typed text box — and both surfaces that collect these values need
 * the same mapping: the workroom's Details panel, editing a ticket that
 * already exists, and the create dialog, collecting them before one does. It
 * lives here so a new field type is taught to the engine once rather than to
 * two places that can then disagree about it.
 *
 * Two events, because a text box and a picker settle at different moments.
 * `update:modelValue` carries every keystroke; `commit` fires only when the
 * value is final — blur or Enter on a text box, and the change itself on a
 * picker or a checkbox, which have no intermediate state to pass through. A
 * caller that saves one field at a time listens to `commit`; a caller
 * collecting a whole form before it writes anything listens to the model.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  // `{ id, key, label, fieldType, options, required, position }`, camelised.
  definition: {
    type: Object,
    required: true,
  },
  modelValue: {
    type: [String, Number, Boolean, Array],
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'sm',
    validator: value => ['sm', 'md'].includes(value),
  },
});

const emit = defineEmits(['update:modelValue', 'commit']);

const { t } = useI18n();

// The id the caller's own <label for> points at, so the label belongs to this
// control without the caller having to know which control it turned out to be.
const inputId = computed(() => `case-field-input-${props.definition.id}`);

const isMultiSelect = computed(
  () => props.definition.fieldType === 'multi_select'
);

const isPicker = computed(
  () => props.definition.fieldType === 'select' || isMultiSelect.value
);

const textType = computed(() => {
  if (props.definition.fieldType === 'number') return 'number';
  if (props.definition.fieldType === 'date') return 'date';
  if (props.definition.fieldType === 'link') return 'url';
  return 'text';
});

// A picker's selection is read off the value rather than bound to the element:
// a `multiple` select ignores a `value` property, so the options have to say
// for themselves which of them are chosen.
const isSelected = option =>
  isMultiSelect.value
    ? (props.modelValue || []).includes(option)
    : props.modelValue === option;

// A picker and a checkbox have no in-between state — the change is the answer.
const settle = value => {
  emit('update:modelValue', value);
  emit('commit', value);
};

const onPick = event => {
  settle(
    isMultiSelect.value
      ? Array.from(event.target.selectedOptions, option => option.value)
      : event.target.value
  );
};
</script>

<template>
  <Checkbox
    v-if="definition.fieldType === 'boolean'"
    :id="inputId"
    :model-value="Boolean(modelValue)"
    :disabled="disabled"
    @update:model-value="settle($event)"
  />

  <select
    v-else-if="isPicker"
    :id="inputId"
    :multiple="isMultiSelect"
    :disabled="disabled"
    class="w-full px-2 py-1.5 text-sm border-0 rounded-lg outline outline-1 outline-offset-[-1px] outline-n-weak bg-n-alpha-black2 text-n-slate-12 disabled:opacity-50"
    @change="onPick"
  >
    <option v-if="!isMultiSelect" value="" :selected="!modelValue">
      {{ t('SUPPORT_CASES.DETAIL.FIELDS.UNSET') }}
    </option>
    <option
      v-for="option in definition.options || []"
      :key="option"
      :value="option"
      :selected="isSelected(option)"
    >
      {{ option }}
    </option>
  </select>

  <Input
    v-else
    :id="inputId"
    :model-value="modelValue ?? ''"
    :size="size"
    :type="textType"
    :disabled="disabled"
    :placeholder="t('SUPPORT_CASES.DETAIL.FIELDS.UNSET')"
    @update:model-value="emit('update:modelValue', $event)"
    @enter="emit('commit', modelValue)"
    @blur="emit('commit', modelValue)"
  />
</template>
